
import React, { createContext, useContext, useState } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const translations = {
  en: {
    // Header
    home: 'Home',
    languages: 'Languages',
    courses: 'Courses',
    about: 'About',
    instructors: 'Instructors',
    successStories: 'Success Stories',
    contact: 'Contact',
    bookFreeTrial: 'Book Free Assessment',
    phone: 'Phone',
    email: 'Email',
    followUs: 'Follow us',

    // Hero Section
    heroTitle: 'Unlock Your Child\'s Future',
    heroSubtitle: 'through languages',
    heroDescription: 'Prepare them for a multilingual future with our professional language instruction: English, French, Italian, Spanish, German, and Portuguese. From beginner level to Cambridge certification, we accompany you every step of the way, fostering confidence, fluency, and new opportunities without borders.',
    startFreeTrial: 'Start Free Assessment',
    watchDemo: 'Watch Demo',
    happyStudents: 'Happy Students',
    successRate: 'Success Rate',
    yearsExperience: 'Years Experience',
    languagesCerts: '6 Languages • Official Certifications',
    languagesTaught: 'Languages Taught',
    certifiedEducation: 'Certified Language Education Since 2009',

    // Languages Section
    //ENGLISH
    nameen: 'English',
    certificationen: 'Cambridge English',
    levelsen: 'A1 - C2',
    featuresen: ['IELTS Preparation', 'Business English', 'Academic Writing'],
    //FRENCH
    namefr: 'French',
    certificationfr: 'DELF/DALF',
    featuresfr: ['French Culture', 'Literature', 'Business French'],
    //SPANISH
    namees: 'Spanish',
    certificationes: 'D.E.L.E.',
    featureses: ['Latin American Spanish', 'Business Spanish', 'Literature'],
    //GERMAN
    namede: 'German',
    certificationde: 'TestDaF',
    featuresde: ['Technical German', 'Academic German', 'Cultural Studies'],
    //ITALIAN
    nameit: 'Italian',
    certificationit: 'CILS/CELI',
    featuresit: ['Italian Culture', 'Art History', 'Business Italian'],
    //PORTUGUESE
    namept: 'Portuguese',
    certificationpt: 'CAPLE',
    featurespt: ['Portuguese', 'European Portuguese', 'Business'],
    learnmore: 'Learn More',

    languagesTitle: 'Master Languages That',
    languagesSubtitle: 'Open Doors Worldwide',
    languagesDescription: 'From Europe to the Americas, we offer comprehensive language programs that lead to internationally recognized certifications.',
    officialCertifications: 'Official Certifications',
    levels: 'Levels',
    students: 'Students',
    learnMore: 'Learn More',
    notSureTitle: 'Not Sure Which Language to Choose?',
    notSureDescription: 'Take our free assessment to find the perfect language path for your child\'s future.',
    takeFreeAssessment: 'Take Free Assessment',

    // Courses Section
    coursesTitle: 'Choose Your Perfect',
    coursesSubtitle: 'Learning Journey',
    coursesDescription: 'Regardless of whether you favor group dynamics, individualized attention, the flexibility of online teaching, or intensive learning, we provide the course format best suited to your learning pace.',
    flexibleLearning: 'Flexible Learning Options',
    groupClasses: 'Group Classes',
    privateLesson: 'Private/Individual Lessons',
    onlineClasses: 'Online classes, allowing freedom in learning.',
    intensiveCourses: 'Intensive Courses',
    mostPopular: 'Most Popular',
    premium: 'Premium',
    flexible: 'Flexible',
    fastTrack: 'Fast Track',
    experience: 'Experience',
    schedule: 'Schedule',
    specialPrograms: 'Special Programs',
    examPreparation: 'Exam Preparation',
    familyPackages: 'Family Packages',
    summerCamps: 'Summer Camps',
    readyToStart: 'Ready to begin your language learning journey?',
    readyDescription: 'Visit us and find the right training, tailored to your needs and goals.',
    bookConsultation: 'Book Free Consultation',
    viewSchedule: 'View Schedule',
    //special programs
    examPreparationdescription: 'Specialized courses for Cambridge, DELF, DELE, TestDaF and other certification exams.',
    familyPackagesdescription: 'For two or more members of the same family, we have an adjustable discount package. Want to learn new languages ​​together? Build relationships and develop skills with us.',
    summerCampsdescription: 'Fun-filled language immersion camps during school holidays with cultural activities.',

    //Courses Section
    subtitlegroup: 'Learn Together, Grow Together',
    descriptiongroup: 'Interactive group sessions with 4-8 students of similar levels. Perfect for developing conversation skills and cultural understanding.',
    featuresgroup: ['8-12 students per class', 'Interactive and motivating activities', 'Cultural exchange', 'Peer learning'],
    durationgroup: 'Full school year system at various levels',
    schedulegroup: '2x per week',
    badgegroup: 'Most Popular',

    titleprivate: 'Private Tutoring',
    subtitleprivate: 'Do you want to learn in an individualized and personal way?',
    descriptionprivate: 'And how do you choose when you learn? Come and discover our offerings and schedules.',
    featuresprivate: ['Personalized curriculum', 'Flexible scheduling', 'Rapid progress', 'Custom materials'],
    durationprivate: 'Flexible',
    scheduleprivate: 'Your choice',
    badgeprivate: 'Premium',

    titleonline: 'Online Classes',
    subtitleonline: 'Do you want to learn at your own pace, from anywhere in the world?',
    descriptiononline: 'We have the best team, available for excellent teaching, at your pace, on your schedule, wherever you are (currently with students in Switzerland, Germany, and Luxembourg). Come discover a new path and learn in the way that best suits you!',
    featuresonline: ['Live interactive sessions', 'Digital materials', 'Recording access', 'Global classroom'],
    durationonline: '8-16 weeks',
    scheduleonline: 'Flexible times',
    badgeonline: 'Flexible',

    titleintensive: 'Intensive Courses',
    subtitleintensive: 'Learning program specially designed for those who need language development.',
    descriptionintensive: 'Immersive programs designed for rapid language acquisition. Perfect for students with specific deadlines or goals.',
    featuresintensive: ['Tailor-made lessons for each student', 'Results according to needs', 'Exam preparation', 'Goal setting in advance'],
    durationintensive: '4-8 weeks',
    scheduleintensive: 'Daily',
    badgeintensive: 'Fast Track',

    // About Section
    aboutTitle: 'Empowering Futures Through',
    aboutSubtitle: 'Teaching Excellence',
    aboutDescription: 'For over 30 years, our adventure has been dedicated to helping students improve their skills and explore, through dynamic and interactive teaching, the various languages ​​we teach.',
    ourStory: 'Our Story & Mission',
    whereLanguage: 'In each new language, a new way of communicating',
    aboutp1: 'Founded in 1993 by a team dedicated to teaching Languages ​​and Computer Science, YorkSchool began with a simple belief: All children deserve the opportunity to communicate confidently in a globalized world.',
    aboutp2: 'Starting as a small school, we have grown into a trusted institution with concrete evidence, accredited by the most important language teaching institutions in the world, with over 5,000 students from a wide variety of countries.',
    aboutp3: 'Our commitment?',
    aboutp4: 'An excellent education, with dedicated teachers committed to the main mission: education is a universal right, with interaction and total availability, for the good of our students.',
    aboutp5: 'Our mission?',
    aboutp6: 'To empower students of all ages to achieve their goals, broaden their horizons, and embrace new cultures with knowledge and confidence.',
    certifiedInstructors: 'Certified Instructors',
    coreValues: 'Our teachers are our main driving force',
    ourJourney: 'Our Journey',
    studentsGraduated: 'Students Graduated',
    yearsOfExcellence: 'Years of Excellence',
    languagesOffered: 'Languages Offered',
    countriesRepresented: 'Countries Represented',
    title1about: 'Passionate Teaching',
    description1about: 'Motivated, committed, and available to all our students, in an inclusive way and adapted to the individual needs of each student. And always focused on the goals defined by each of our students.',
    title2about: 'Goal-Oriented',
    description2about: 'For and because of you. Because together we are better, and because together it\'s easier!',
    title3about: 'The Importance of Multiculturalism',
    description3about: 'Come learn with us and forge a path through the most important languages, transporting us to other realities and cultures. Be a part of globalization through unforgettable experiences and unique knowledge.',
    title4about: 'Proven Results',
    description4about: 'Our students consistently and effectively achieve the certification goals of the most renowned language institutes, acquiring solid knowledge that gives them confidence and security. Learning the language and computer skills taught at YorkSchool opens up a wide range of unique and unmissable opportunities.',
    // Instructors Section
    instructorsTitle: 'Learn from the',
    instructorsSubtitle: 'Best in the Field',
    instructorsDescription: 'Our certified instructors are passionate educators with advanced degrees, official certifications, and years of experience helping students achieve their language goals.',
    expertInstructors: 'Expert Instructors',
    readyToMeet: 'Ready to Meet Your Perfect Instructor?',
    readyToMeetDescription: 'Book a free consultation to match with the instructor who best fits your learning style and goals.',
    meetInstructors: 'Meet Our Instructors',
    scheduleConsultation: 'Schedule Consultation',

    // Testimonials Section
    testimonialsTitle: 'Real Students,',
    testimonialsSubtitle: 'Real Success',
    certificationPassRate: 'Certification Pass Rate',
    exceedTargetLevel: 'Exceed Target Level',
    studentSatisfaction: 'Student Satisfaction',
    completePrograms: 'Complete Programs',

    // News Section
    newsTitle: 'Stay Connected with',
    newsSubtitle: 'YorkSchool Community',
    newsDescription: 'Get the latest updates on courses, student achievements, events, and educational insights from our language learning community.',
    latestNews: 'Latest News & Updates',
    neverMiss: 'Never Miss an Update!',
    neverMissDescription: 'Subscribe to our newsletter and get the latest news, course updates, and learning tips delivered straight to your inbox.',
    subscribeNow: 'Subscribe Now',
    successfullySubscribed: 'Successfully Subscribed!',
    subscribeSuccess: 'You\'ll receive our latest updates and news.',

    // Contact Section
    contactTitle: 'Start Your Language',
    contactSubtitle: 'Journey Today',
    contactDescription: 'Ready to begin? Contact us for a free consultation and let\'s find the perfect language learning path for your child\'s bright future.',
    getInTouch: 'Get In Touch',
    bookConsultationForm: 'Book Your Free Consultation',
    consultationDescription: 'Fill out this form and we\'ll get back to you within 24 hours to schedule your personalized consultation.',
    parentName: 'Parent\'s Name',
    emailAddress: 'Email Address',
    phoneNumber: 'Phone Number',
    languageInterest: 'Language of Interest',
    courseType: 'Preferred Course Type',
    tellUsGoals: 'Tell us about your goals',
    sendMessage: 'Send Message',
    visitSchool: 'Visit Our School',
    callUs: 'Call Us',
    callNow: 'Call Now',
    emailUs: 'Email Us',
    response24: 'We typically respond within 24 hours',
    sendEmail: 'Send Email',
    onlineSupport: 'Online Support',
    quickActions: 'Quick Actions',
    scheduleVisit: 'Schedule a Visit',
    liveChatSupport: 'Live Chat Support',

    // Footer
    stayUpdated: 'Stay Updated with YorkSchool',
    stayUpdatedDescription: 'Get the latest news, language learning tips, and exclusive offers delivered to your inbox.',
    subscribe: 'Subscribe',
    joinFamilies: 'Join 500+ families already subscribed. Unsubscribe anytime.',
    certificationPartners: 'Official Certification Partners',
    privacyPolicy: 'Privacy Policy',
    termsOfService: 'Terms of Service',
    cookiePolicy: 'Cookie Policy',
    allRightsReserved: 'All rights reserved.',

    // Common
    from: 'From',
    duration: 'Duration',
    price: 'Price',
    month: 'month',
    hour: 'hour',
    year: 'year',
    years: 'years',
    months: 'months',
    weeks: 'weeks',
    openingHours: 'Mon-Fri: 10:30 - 13:00 and 13h45 - 19h30',
    fullName: 'Your full name',
    parentName: 'Parent\'s name',
    emailAddressform: 'Email address',
    phoneNumberform: 'Phone number',
    exampleEmail: 'your.email@example.com',
    phoneNumberformnumber: '+351 912 345 678',
    languageInterest: 'Language of interest',
    preferredCourse: 'Preferred course type',
    selectLanguage: 'Select a language',
    selectcourseType: 'Select a course type',
  },
  pt: {
    // Header
    home: 'Início',
    languages: 'Idiomas',
    courses: 'Cursos',
    about: 'Sobre',
    instructors: 'Professores',
    successStories: 'Histórias de Sucesso',
    contact: 'Contato',
    bookFreeTrial: 'Agendar Aula Gratuita',
    phone: 'Telefone',
    email: 'E-mail',
    followUs: 'Siga-nos',

    // Hero Section
    heroTitle: 'Desperte o futuro do',
    heroSubtitle: ' seu filho através das línguas',
    heroDescription: 'Prepare-o para um futuro multilingue com o nosso ensino profissional de idiomas: inglês, francês, italiano, espanhol, alemão e português. Desde o nível inicial até à certificação Cambridge, acompanhamos cada passo da sua aprendizagem, promovendo confiança, fluência e novas oportunidades sem fronteiras.',
    startFreeTrial: 'Marcar Teste Gratuito',
    watchDemo: 'Assistir Demonstração',
    happyStudents: 'Alunos Felizes',
    successRate: 'Taxa de sucesso',
    yearsExperience: 'Anos de Experiência',
    languagesTaught: 'Idiomas Ensinados',
    languagesCerts: '6 Línguas • Certificações Oficiais',
    certifiedEducation: 'Educação Certificada em Idiomas Desde 2009',

    // Languages Section
    //ENGLISH
    nameen: 'Inglês',
    certificationen: 'Inglês Cambridge',
    featuresen: ['Preparação IELTS', 'Inglês para Negócios', 'Escrita Académica'],
    //FRENCH
    namefr: 'Francês',
    certificationfr: 'DELF/DALF',
    featuresfr: ['Cultura Francesa', 'Literatura', 'Francês para Negócios'],
    //SPANISH
    namees: 'Espanhol',
    certificationes: 'D.E.L.E.',
    featureses: ['Espanhol América Latina', 'Espanhol para Negócios', 'Literatura'],
    //GERMAN
    namede: 'Alemão',
    certificationde: 'TestDaF',
    featuresde: ['Alemão Técnico', 'Alemão Académico', 'Estudos Culturais'],
    //ITALIAN
    nameit: 'Italiano',
    certificationit: 'CILS/CELI',
    featuresit: ['Cultura Italiana', 'História das Artes', 'Italiano para Negócios'],
    //PORTUGUESE
    namept: 'Português',
    certificationpt: 'CAPLE',
    featurespt: ['Português', 'Português Europeu', 'Português para Negócios'],
    learnmore: 'Saiba Mais',

    languagesTitle: 'Domine idiomas,',
    languagesSubtitle: 'abra portas em todo o mundo',
    languagesDescription: 'Da Europa às Américas, oferecemos programas completos de línguas que conduzem a certificações reconhecidas internacionalmente.',
    officialCertifications: 'Certificações Oficiais',
    levels: 'Níveis',
    students: 'Alunos',
    learnMore: 'Saiba Mais',
    notSureTitle: 'Não Tem Certeza de Qual Idioma Escolher?',
    notSureDescription: 'Faça nossa avaliação gratuita para encontrar o caminho de idioma perfeito para o futuro do seu filho.',
    takeFreeAssessment: 'Fazer Avaliação Gratuita',

    // Courses Section
    coursesTitle: 'Escolha a Sua',
    coursesSubtitle: 'Jornada de Aprendizagem Perfeita',
    coursesDescription: 'Independentemente de privilegiar a dinâmica de grupo, a atenção individualizada, a flexibilidade do ensino online ou a aprendizagem intensiva, disponibilizamos o formato de curso mais adequado ao seu ritmo de aprendizagem.',
    flexibleLearning: 'Opções de Aprendizagem Flexíveis',
    groupClasses: 'Aulas em Grupo',
    privateLesson: 'Aulas Particulares/Individuais',
    onlineClasses: 'Aulas online, permitindo liberdade na aprendizagem',
    intensiveCourses: 'Cursos Intensivos',
    mostPopular: 'Mais Popular',
    premium: 'Premium',
    flexible: 'Flexível',
    fastTrack: 'Via Rápida',
    experience: 'Experiência',
    schedule: 'Horário',
    specialPrograms: 'Programas Especiais',
    examPreparation: 'Preparação para Exames',
    familyPackages: 'Pacotes Familiares',
    summerCamps: 'Campos de Verão',
    readyToStart: 'Pronto para começar a sua viagem e aprendizagem linguística?',
    readyDescription: 'Visite-nos e encontre a formação certa  e adequada às suas necessidades e objetivos.',
    bookConsultation: 'Agendar Consulta Gratuita',
    viewSchedule: 'Ver Cronograma',
    //special programs
    examPreparationdescription: 'Cursos especializados para Cambridge, DELF, DELE, TestDaF e outros exames de certificação.',
    familyPackagesdescription: 'Para dois ou mais membros da mesma família, temos um pacote de descontos ajustável. Querem aprender em conjunto novos idiomas? Crie laços e desenvolva habilidades connosco.',
    summerCampsdescription: 'Campos de imersão linguística divertidos durante as férias escolares com atividades culturais.',


    //Courses Section
    subtitlegroup: 'Com uma energia muito própria, aprendam e cresçam juntos',
    descriptiongroup: 'Sessões interativas e inclusivas, em grupo, com alunos de níveis semelhantes, permitindo adquirir mais e melhores conhecimentos. A melhor forma de desenvolver habilidades de conversação e compreensão cultural.',
    featuresgroup: ['8-12 alunos por aula', 'Atividades interativas e motivadoras', 'Intercâmbio cultural', 'Aprendizagem entre pares'],
    durationgroup: 'Regime de ano letivo completo nos vários níveis',
    schedulegroup: '2x por semana',
    badgegroup: 'Mais popular',

    titleprivate: 'Aulas Particulares',
    subtitleprivate: 'Quer aprender de uma forma individualizada e pessoal?',
    descriptionprivate: 'E escolher o tempo em que aprende? Venha conhecer a nossa oferta e os nossos horários.',
    featuresprivate: ['Currículo personalizado', 'Agendamento flexível', 'Progresso Rápido', 'Materiais personalizados'],
    durationprivate: 'Flexível',
    scheduleprivate: 'À sua escolha',
    badgeprivate: 'Premium',

    titleonline: 'Aulas online',
    subtitleonline: 'Quer aprender ao seu ritmo, estando em qualquer local do mundo?',
    descriptiononline: 'Temos a melhor equipa, disponível para uma ensino de excelência, ao seu ritmo, no seu horário, esteja onde estiver (atualmente, com alunos na Suíça, Alemanha, Luxemburgo). Venha descobrir um caminho novo e aprender da forma mais adequada para si!',
    featuresonline: ['Sessões interativas ao vivo', 'Materiais digitais', 'Acesso à gravação', 'Sala de aula global'],
    durationonline: '8-16 semanas',
    scheduleonline: 'Horários flexíveis',
    badgeonline: 'Flexível',

    titleintensive: 'Cursos Intensivos',
    subtitleintensive: 'Aprendizagem preparada especialmente para quem necessita de um desenvolvimento linguístico.',
    descriptionintensive: 'Programas imersivos projetados para aquisição rápida do idioma. Perfeitos para alunos com prazos ou objetivos específicos.',
    featuresintensive: ['Aulas à medida de cada aluno', 'Resultados de acordo com as necessidades', 'Preparação para exames', 'Definição antecipada dos objetivos'],
    durationintensive: '4-8 semanas',
    scheduleintensive: 'Diárias',
    badgeintensive: 'Via rápida',

    // About Section
    aboutTitle: 'Capacitamos Futuros Através da',
    aboutSubtitle: 'Excelência de Ensino',
    aboutDescription: 'Há mais de 30 anos que começou a nossa aventura, dedicada a  ajudar os alunos a potenciar as suas capacidades e a explorar, através de um ensino dinâmico e interativo, nos vários idiomas que lecionamos.',
    ourStory: 'A Nossa História & Missão',
    whereLanguage: 'Em cada novo idioma, uma nova forma de comunicar',
    aboutp1: 'Fundada em 1993 por uma equipa dedicada ao ensino de Línguas e Informática, a YorkSchool começou com uma crença simples: Todas as crianças merecem a oportunidade de comunicarem com confiança num mundo globalizado.',
    aboutp2: 'Começando como uma pequena escola, crescemos somos atualmente uma instituição confiável e com provas concretas, com acreditações das instituições mais importantes no mundo do ensino de línguas, com mais de 5.000 alunos dos mais variados países (com alunos dos mais variados países).',
    aboutp3: 'O nosso compromisso?',
    aboutp4: 'Uma educação de excelência, com professores dedicados e comprometidos com a missão principal:  o ensino é um direito universal, com interação e disponibilidade total, pelo bem dos nossos alunos.',
    aboutp5: 'A nossa missão?',
    aboutp6: 'Capacitar alunos, de todas as idades, a atingir os seus objetivos, ampliar os seus horizontes, abraçando  novas culturas com conhecimento e confiança.',
    certifiedInstructors: 'Professores Certificados',
    title1about: 'Ensino Apaixonado',
    description1about: 'Motivados, Empenhados e Disponíveis para todos os nossos alunos, de forma inclusiva e adaptada às necessidades individuais de cada aluno. E sempre orientados para os objetivos definidos por cada um dos nossos estudantes.',
    title2about: 'Orientado a objetivos',
    description2about: 'Para e por si. Porque todos juntos somos melhores, e porque juntos é mais fáci!',
    title3about: 'A Importância da Multiculturalidade',
    description3about: 'Venha aprender connosco e conquistar um caminho, através dos idiomas mais importantes, que nos transportam a outras realidades e outras culturas. Seja parte da Globalização Mundial, através de experiências inesquecíveis e conhecimentos únicos.',
    title4about: 'Resultados Comprovados',
    description4about: 'Os nossos alunos atingem, de forma consistente e objetiva, as metas de certificação dos mais conceituados institutos de línguas, adquirindo conhecimentos sólidos que lhes conferem confiança e segurança. A aprendizagem de competências linguísticas e informáticas lecionadas na YorkSchool abre um vasto leque de oportunidades únicas e imperdíveis.',

    coreValues: 'Os nossos professores são a nossa força motora principal',
    ourJourney: 'A Nossa Jornada',
    studentsGraduated: 'Alunos Formados',
    yearsOfExcellence: 'Anos de Excelência',
    languagesOffered: 'Idiomas Oferecidos',
    countriesRepresented: 'Países Representados',

    // Instructors Section
    instructorsTitle: 'Aprenda com os',
    instructorsSubtitle: 'Melhores da Área',
    instructorsDescription: 'Nossos instrutores certificados são educadores apaixonados com diplomas avançados, certificações oficiais e anos de experiência ajudando estudantes a alcançar seus objetivos linguísticos.',
    expertInstructors: 'Instrutores Especialistas',
    readyToMeet: 'Pronto para Conhecer Seu Instrutor Perfeito?',
    readyToMeetDescription: 'Agende uma consulta gratuita para combinar com o instrutor que melhor se adapta ao seu estilo de aprendizagem e objetivos.',
    meetInstructors: 'Conheça Nossos Instrutores',
    scheduleConsultation: 'Agendar Consulta',

    // Testimonials Section
    testimonialsTitle: 'Alunos Reais,',
    testimonialsSubtitle: 'Sucesso Real',
    testimonialsDescription: 'Ouça de pais cujos filhos alcançaram seus objetivos de aprendizagem de idiomas e obtiveram certificações reconhecidas internacionalmente com nossos programas.',
    successStories: 'Histórias de Sucesso',
    certificationPassRate: 'Taxa de Aprovação em Certificações',
    exceedTargetLevel: 'Excedem Nível Alvo',
    studentSatisfaction: 'Satisfação dos Alunos',
    completePrograms: 'Completam Programas',
    yourChildSuccess: 'A História de Sucesso do Seu Filho Começa Aqui',
    yourChildDescription: 'Junte-se a centenas de famílias que alcançaram seus objetivos de aprendizagem de idiomas conosco.',
    startYourJourney: 'Comece Sua Jornada',
    readMoreStories: 'Ler Mais Histórias',

    // News Section
    newsTitle: 'Mantenha-se Conectado com a',
    newsSubtitle: 'Comunidade YorkSchool',
    newsDescription: 'Receba as últimas atualizações sobre cursos, conquistas dos alunos, eventos e insights educacionais de nossa comunidade de aprendizagem de idiomas.',
    latestNews: 'Últimas Notícias & Atualizações',
    neverMiss: 'Nunca Perca uma Atualização!',
    neverMissDescription: 'Inscreva-se em nossa newsletter e receba as últimas notícias, atualizações de cursos e dicas de aprendizagem diretamente em sua caixa de entrada.',
    subscribeNow: 'Inscrever-se Agora',
    successfullySubscribed: 'Inscrito com Sucesso!',
    subscribeSuccess: 'Você receberá nossas últimas atualizações e notícias.',

    // Contact Section
    contactTitle: 'Comece a Sua Jornada',
    contactSubtitle: 'de Idiomas Hoje',
    contactDescription: 'Pronto para começar? Entre em contato conosco para uma consulta gratuita e vamos encontrar o caminho perfeito de aprendizagem de idiomas para o futuro brilhante do seu filho.',
    getInTouch: 'Entre em Contato',
    bookConsultationForm: 'Agende Sua Consulta Gratuita',
    consultationDescription: 'Preencha este formulário e entraremos em contato até 24 horas para agendar a sua consulta personalizada.',
    parentName: 'Nome do Responsável',
    emailAddress: 'Endereço de E-mail',
    phoneNumber: 'Número de Telefone',
    languageInterest: 'Idioma de Interesse',
    courseType: 'Tipo de Curso Preferido',
    tellUsGoals: 'Conte-nos sobre seus objetivos',
    sendMessage: 'Enviar Mensagem',
    visitSchool: 'Visite a Nossa Escola',
    callUs: 'Ligue-nos',
    callNow: 'Ligar Agora',
    emailUs: 'Envie-nos um E-mail',
    sendEmail: 'Enviar Email',
    response24: 'Normalmente respondemos dentro de 24 horas',
    onlineSupport: 'Suporte Online',
    quickActions: 'Ações rápidas',
    scheduleVisit: 'Agendar uma Visita',
    liveChatSupport: 'Suporte por Chat ao Vivo',

    // Footer
    stayUpdated: 'Mantenha-se Atualizado com a YorkSchool',
    stayUpdatedDescription: 'Receba as últimas notícias, dicas de aprendizagem de idiomas e ofertas exclusivas em sua caixa de entrada.',
    subscribe: 'Inscrever-se',
    joinFamilies: 'Junte-se a mais de 500 famílias já inscritas. Cancele a inscrição a qualquer momento.',
    certificationPartners: 'Parceiros de Certificação Oficiais',
    privacyPolicy: 'Política de Privacidade',
    termsOfService: 'Termos de Serviço',
    cookiePolicy: 'Política de Cookies',
    allRightsReserved: 'Todos os direitos reservados.',

    // Common
    from: 'A partir de',
    duration: 'Duração',
    price: 'Preço',
    month: 'mês',
    hour: 'hora',
    year: 'ano',
    years: 'anos',
    months: 'meses',
    weeks: 'semanas',
    openingHours: 'Seg-Sex: 10:30 - 13:00 e 13h45 - 19h30',
    fullName: 'Nome completo',
    parentName: 'Nome do responsável',
    emailAddressform: 'Endereço de e-mail',
    phoneNumberform: 'Número de telefone',
    exampleEmail: 'seu.email@examplo.pt',
    phoneNumberformnumber: '+351 912 345 678',
    languageInterest: 'Idioma de interesse',
    preferredCourse: 'Tipo de curso preferido',
    selectLanguage: 'Selecione um idioma',
    selectcourseType: 'Selecione um tipo de curso',
  }
};

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState('pt');

  const toggleLanguage = () => {
    setCurrentLanguage(prev => prev === 'en' ? 'pt' : 'en');
  };

  const t = (key) => {
    return translations[currentLanguage][key] || key;
  };

  const value = {
    currentLanguage,
    toggleLanguage,
    t
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
